import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MapPin } from "lucide-react";

/**
 * Address input with typeahead suggestions from Nominatim (via backend proxy).
 * On selection, sets the display_name (a geocode-ready address) into value.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  required = false,
  testId = "input-address",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [justPicked, setJustPicked] = useState(false);
  const boxRef = useRef(null);

  // debounce fetch
  useEffect(() => {
    if (justPicked) { setJustPicked(false); return; }
    const q = (value || "").trim();
    if (q.length < 3) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/address/suggest?q=${encodeURIComponent(q)}`);
        setSuggestions(res.data?.suggestions || []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  // click outside to close
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const pick = (s) => {
    setJustPicked(true);
    onChange(s.label);
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        data-testid={testId}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={onKey}
        className="input-dark w-full"
        placeholder={placeholder}
        autoComplete="off"
      />
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-cyan-300/60">…</div>
      )}
      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testId}-suggestions`}
          className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-cyan-400/25 bg-[#0c1825] shadow-2xl shadow-cyan-500/10"
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              data-testid={`${testId}-suggestion-${i}`}
              onPointerDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`cursor-pointer px-3 py-3 text-sm flex gap-2 items-start border-b border-white/5 last:border-0 touch-manipulation ${i === highlight ? "bg-cyan-400/10 text-white" : "text-slate-200 hover:bg-white/5 active:bg-cyan-400/20"}`}
            >
              <MapPin className="mt-0.5 flex-shrink-0 h-4 w-4 text-cyan-300" />
              <span className="leading-snug">{s.label}</span>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && suggestions.length === 0 && (value || "").trim().length >= 3 && (
        <div
          data-testid={`${testId}-no-results`}
          className="absolute z-20 mt-1 w-full rounded-lg border border-cyan-400/20 bg-[#0c1825] px-4 py-3 text-xs text-slate-400 shadow-xl"
        >
          No matches yet — keep typing, or try adding the city + state (e.g. <span className="text-cyan-300">&quot;123 Main St, Miami, FL&quot;</span>). We can still take your booking either way.
        </div>
      )}
    </div>
  );
}
